/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/react-in-jsx-scope */
import React, { useState, useCallback, useEffect } from "react";
import "./GSAExitHandler.css";
/**
 * GSAExitHandler — Reusable exit flow for all GSA screens (React/web).
 *
 * Covers: all GSA question screens + Personalize screen.
 * Does NOT cover: Movement test screens (those use the Flutter/Dart class).
 *
 * Usage:
 *   <GSAExitHandler
 *     isOpen={showExit}
 *     gsaStep="nprs_symptoms"
 *     commMode="chat"            // "chat" | "voice" | null
 *     onClose={() => setShowExit(false)}
 *     onExit={() => navigateToLanding()}
 *     onOpenMyraChat={(message) => openChatPanel(message)}
 *     onOpenMyraVoice={(message) => openVoicePanel(message)}
 *     onSetCommMode={(mode) => updateCommMode(mode)}
 *     onAnalyticsEvent={(event) => trackEvent(event)}
 *   />
 */

// ─── SCREEN CONTEXT DETECTION ───
const PERSONALIZE_STEPS = ["personalize"];
const MOVEMENT_TEST_STEPS = ["movement_test_1", "movement_test_2"];

function getContextType(gsaStep) {
  if (PERSONALIZE_STEPS.includes(gsaStep)) return "personalize";
  if (MOVEMENT_TEST_STEPS.includes(gsaStep)) return "movement_test";
  return "gsa";
}

// ─── TILE CONFIGURATIONS ───
// Each context defines: heading, subheading, and an array of tiles.
// Tiles with myra:true route to Myra. Tiles with id:'leave' go to feedback.

const CONFIGS = {
  gsa: {
    heading: "Having Trouble or Thinking About Leaving?",
    subheading: "Let us know if you need help or want to exit — we\u2019re here to support you.",
    tiles: [
      {
        id: "confused",
        label: "I\u2019m not sure how to answer",
        description: "Not sure what fits your situation? Myra can help with any questions you have.",
        myra: true,
      },
      {
        id: "technical",
        label: "Are you having technical difficulties?",
        description: "If something isn\u2019t working, Myra can help troubleshoot the issue.",
        myra: true,
      },
      {
        id: "leave",
        label: "I want to leave the assessment",
        description: "If you decide to leave, we\u2019d love to hear how we can improve.",
        myra: false,
      },
    ],
  },
  personalize: {
    heading: "We Can Help or Want to Exit?",
    subheading: "This information helps us create your personalized recovery plan.",
    tiles: [
      {
        id: "why_info",
        label: "Why do you need this information?",
        description: "Curious why we\u2019re asking for age, weight, or other details? Myra can explain everything.",
        myra: true,
      },
      {
        id: "confused",
        label: "I\u2019m not sure how to answer",
        description: "Not sure what to put? Myra can guide you through any question or field.",
        myra: true,
      },
      {
        id: "technical",
        label: "Something isn\u2019t working",
        description: "If the screen or buttons aren\u2019t responding, Myra can help troubleshoot the issue.",
        myra: true,
      },
      {
        id: "leave",
        label: "I want to leave the assessment",
        description: "If you decide to stop, we\u2019d love to hear how we can improve your experience.",
        myra: false,
      },
    ],
  },
  movement_test: {
    heading: "Tracking Trouble or Want to Exit?",
    subheading: "Let us know if you need help or want to exit — we\u2019re here to support you.",
    tiles: [
      {
        id: "movement_test_1",
        label: "Tracking isn't working correctly",
        description: "Having trouble with the camera or body tracking? Myra can help troubleshoot.",
        myra: true,
      },
      {
        id: "confused",
        label: "I\u2019m not sure how to answer",
        description: "Not sure what to put? Myra can guide you through any question or field.",
        myra: true,
      },
      {
        id: "technical",
        label: "Something isn\u2019t working",
        description: "If the screen or buttons aren\u2019t responding, Myra can help troubleshoot the issue.",
        myra: true,
      },
      {
        id: "leave",
        label: "I want to leave the assessment",
        description: "If you decide to stop, we\u2019d love to hear how we can improve your experience.",
        myra: false,
      },
    ],
  },
};

// ─── DETERMINISTIC MYRA FIRST MESSAGES ───
// These are the exact messages Myra sends when the chat/voice panel opens.
// No Claude call needed — these are hardcoded per reason.

const DETERMINISTIC_MESSAGES = {
  confused: {
    chat: "No worries \u2014 what part is unclear? I\u2019m happy to walk you through it.",
    voice: "Tell me what\u2019s confusing and I\u2019ll walk you through it.",
  },
  technical: {
    chat: "Sorry about that. Can you tell me what\u2019s happening \u2014 is the screen frozen, or is something not responding?",
    voice: "Tell me what\u2019s not working and I\u2019ll try to help.",
  },
  why_info: {
    chat: "Great question. Your age, weight, and other details help me choose exercises at the right intensity and make sure they\u2019re safe. What specifically are you wondering about?",
    voice: "Your age and biometrics help me personalize exercises that are safe and effective for you. What would you like to know more about?",
  },
  movement_test_1: {
    chat: "I see you're having trouble with the tracking. Are the targets not showing up, or is the camera not picking up your body?",
    voice: "I see you're having trouble with the tracking. Tell me what's happening.",
  },
};

// ─── SVG ICONS ───
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
  </svg>
);

const VoiceIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z" />
  </svg>
);

const ExitIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="#dc2626">
    <path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
  </svg>
);

// ─── MODE ICON RESOLVER ───
function ModeIcon({ commMode }) {
  if (commMode === "chat") return <ChatIcon />;
  if (commMode === "voice") return <VoiceIcon />;
  // No mode set — show both icons as hint
  return (
    <span style={{ display: "flex", gap: 2, alignItems: "center" }}>
      <ChatIcon />
      <VoiceIcon />
    </span>
  );
}

// ─── STEPS ───
const STEPS = {
  REASONS: "reasons",
  MODE_PICK: "mode_pick",
  LEAVING: "leaving",
};

// ─── COMPONENT ───
export default function GSAExitHandler({ isOpen, gsaStep, commMode, onClose, onExit, onOpenMyraChat, onOpenMyraVoice, onSetCommMode, onAnalyticsEvent }) {
  const [step, setStep] = useState(STEPS.REASONS);
  const [reason, setReason] = useState(null);
  const [feedback, setFeedback] = useState("");

  const contextType = getContextType(gsaStep);
  const config = CONFIGS[contextType];
  const lastSentMessageRef = React.useRef(null);

  // Reset state when overlay opens
  useEffect(() => {
    if (isOpen) {
      setStep(STEPS.REASONS);
      setReason(null);
      setFeedback("");
    }
  }, [isOpen]);

  // ── TILE TAP HANDLER ──
  const handleTileTap = useCallback(
    (tile) => {
      window.optionSelected = true;
      if (tile.id === "leave") {
        setReason("want_to_leave");
        setStep(STEPS.LEAVING);
        return;
      }

      setReason(tile.id);

      // Route based on communication mode
      if (commMode === "chat") {
        const msg = DETERMINISTIC_MESSAGES[tile.id]?.chat;
        onClose(); // Close exit overlay
        const isMovementTest = gsaStep === "movement_test_1";

        // Allow always if movement_test_1, otherwise prevent duplicates
        if (isMovementTest || lastSentMessageRef.current !== msg) {
          onOpenMyraChat(msg);
        }
        lastSentMessageRef.current = msg;

        fireAnalytics(tile.id, "MYRA_CHAT");
      } else if (commMode === "voice") {
        const msg = DETERMINISTIC_MESSAGES[tile.id]?.voice;
        onClose();
        onOpenMyraVoice(msg);
        fireAnalytics(tile.id, "MYRA_VOICE");
      } else {
        // No mode selected — show mode picker
        setStep(STEPS.MODE_PICK);
      }
    },
    [commMode, onClose, onOpenMyraChat, onOpenMyraVoice, gsaStep],
  );

  // ── MODE PICK HANDLER ──
  const handleModePick = useCallback(
    (mode) => {
      onSetCommMode(mode); // Set mode globally for the session

      if (mode === "chat") {
        const msg = DETERMINISTIC_MESSAGES[reason]?.chat;
        onClose();
        const isMovementTest = gsaStep === "movement_test_1";
        if (lastSentMessageRef.current !== msg || isMovementTest) {
          onOpenMyraChat(msg); // Open chat panel with deterministic message
        }
        lastSentMessageRef.current = msg;

        fireAnalytics(reason, "MYRA_CHAT");
      } else if (mode === "voice") {
        const msg = DETERMINISTIC_MESSAGES[reason]?.voice;
        onClose();
        onOpenMyraVoice(msg);
        fireAnalytics(reason, "MYRA_VOICE");
      }
    },
    [reason, onClose, onOpenMyraChat, onOpenMyraVoice, onSetCommMode],
  );

  // ── EXIT CONFIRM ──
  const handleConfirmExit = useCallback(() => {
    fireAnalytics(reason || "want_to_leave", "EXIT");
    onExit();
  }, [reason, feedback, onExit]);

  // ── ANALYTICS ──
  const fireAnalytics = useCallback(
    (exitReason, outcome) => {
      if (onAnalyticsEvent) {
        onAnalyticsEvent({
          event_name: "gsa_exit",
          gsa_step: gsaStep,
          context_type: contextType,
          reason: exitReason,
          comm_mode: commMode || "none",
          feedback: feedback || null,
          outcome,
          timestamp: new Date().toISOString(),
        });
      }
    },
    [gsaStep, contextType, commMode, feedback, onAnalyticsEvent],
  );

  if (!isOpen) return null;
  const handleOnClose = (event) => {
    if (event.target !== event.currentTarget) return;
    window.optionSelected = false;
    window.resumeNoCamera = false;
    onClose();
  };
  return (
    // use onMouseDown to capture the event earlier, preventing the same event from closing it on android
    <div className="exit-overlay" onClick={handleOnClose}>
      <div className="exit-card">
        {/* ── STEP: Reason tiles ── */}
        {step === STEPS.REASONS && (
          <div className="eh-step-content">
            <h2 className="eh-heading">{config.heading}</h2>
            <p className="eh-subheading">{config.subheading}</p>

            <div className="eh-tiles">
              {config.tiles.map((tile) => (
                <button key={tile.id} className="eh-tile" onClick={() => handleTileTap(tile)}>
                  <div className="eh-tile-icon">{tile.myra ? <ModeIcon commMode={commMode} /> : tile.id === "leave" ? <ExitIcon /> : null}</div>
                  <div className="eh-tile-text">
                    <div className="eh-tile-label">{tile.label}</div>
                    <div className="eh-tile-desc">{tile.description}</div>
                  </div>
                </button>
              ))}
            </div>

            <button className="eh-back-link" 
            onClick={() => {
              window.optionSelected = false;
              window.resumeNoCamera = true;
              onClose();
            }}
            >
              I changed my mind — take me back.
            </button>
          </div>
        )}

        {/* ── STEP: Mode picker (when no chat/voice selected) ── */}
        {step === STEPS.MODE_PICK && (
          <div className="eh-step-content">
            <div className="eh-myra-header">
              <div className="eh-myra-avatar">M</div>
              <div>
                <span className="eh-myra-name">Myra</span>
                <span className="eh-myra-status">can help with this</span>
              </div>
            </div>

            <p className="eh-mode-prompt">How would you like to talk to Myra?</p>

            <button className="eh-tile" onClick={() => handleModePick("chat")}>
              <div className="eh-tile-icon">
                <ChatIcon />
              </div>
              <div className="eh-tile-text">
                <div className="eh-tile-label">Chat with Myra</div>
                <div className="eh-tile-desc">Type your messages — no microphone needed.</div>
              </div>
            </button>

            <button className="eh-tile" onClick={() => handleModePick("voice")}>
              <div className="eh-tile-icon">
                <VoiceIcon />
              </div>
              <div className="eh-tile-text">
                <div className="eh-tile-label">Speak to Myra</div>
                <div className="eh-tile-desc">Speak naturally — just needs microphone access.</div>
              </div>
            </button>

            <button className="eh-back-link" onClick={() => setStep(STEPS.REASONS)}>
              Go back
            </button>
          </div>
        )}

        {/* ── STEP: Leaving — feedback capture ── */}
        {step === STEPS.LEAVING && (
          <div className="eh-step-content">
            <h2 className="eh-heading">Before you go</h2>
            <p className="eh-leaving-text">Your progress won't be saved, but you can restart anytime.</p>

            <textarea className="eh-feedback" placeholder="How can we improve? (optional)" value={feedback} onChange={(e) => setFeedback(e.target.value)} />

            <button className="eh-btn-primary" onClick={handleConfirmExit}>
              Exit to landing page
            </button>

            <button className="eh-back-link" onClick={() => setStep(STEPS.REASONS)}>
              Stay here
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
